import React, { useState, useMemo, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'; // kept so the rest of your app keeps working
import './Datatable.css';

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

// Re-renders when a CSS media query starts/stops matching
const useMediaQuery = (query) => {
  const getMatch = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange); // older Safari
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);

  return matches;
};

const range = (from, to) => Array.from({ length: Math.max(to - from + 1, 0) }, (_, i) => from + i);

// Returns e.g. [1, 'start-dots', 4, 5, 6, 'end-dots', 20]
const getPageItems = (current, total, siblings) => {
  const totalNumbers = siblings * 2 + 5;
  if (total <= totalNumbers) return range(1, total);

  const left = Math.max(current - siblings, 1);
  const right = Math.min(current + siblings, total);
  const showLeftDots = left > 2;
  const showRightDots = right < total - 1;
  const edgeCount = 3 + 2 * siblings;

  if (!showLeftDots && showRightDots) return [...range(1, edgeCount), 'end-dots', total];
  if (showLeftDots && !showRightDots) return [1, 'start-dots', ...range(total - edgeCount + 1, total)];
  return [1, 'start-dots', ...range(left, right), 'end-dots', total];
};

const isPrimitive = (v) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

const colId = (column, index) => column.id ?? column.name ?? `col-${index}`;

const compareValues = (a, b) => {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1; // empty values always last
  if (bEmpty) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
/**
 * columns[] : { name, selector(row), id?, align?, width?, maxWidth?, style?, bgColor?,
 *               sortable? (default true), sortValue?(row), filterValue?(row),
 *               isTitle? (card heading on mobile), hideOnMobile?, mobileLabel? }
 *             A column with an empty name is treated as an "actions" column:
 *             on mobile it is shown as a button row at the bottom of the card.
 * innerColumns[] : { name, selector(nestedRow), align?, width?, bgColor?, rowSpan? }
 * data[]    : rows. Optional: id, bgColor, nestedData[]
 * breakpoint: px width below which cards are used (default 768)
 */
const Datatable = ({ columns = [], data = [], innerColumns = [], breakpoint = 768 }) => {
  const isMobile = useMediaQuery(`(max-width: ${breakpoint - 1}px)`);

  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const [expanded, setExpanded] = useState({}); // mobile: which cards show nested details

  /* ---------- sorting ---------- */
  const sortableColumns = useMemo(
    () =>
      columns
        .map((column, index) => ({ column, id: colId(column, index) }))
        .filter(({ column }) => column.name && column.sortable !== false),
    [columns]
  );

  const activeSortColumn = useMemo(
    () => sortableColumns.find(({ id }) => id === sortConfig.key)?.column || null,
    [sortableColumns, sortConfig.key]
  );

  const getSortValue = (column, row) => {
    const v = column.sortValue ? column.sortValue(row) : column.selector(row);
    return isPrimitive(v) ? v : ''; // JSX (buttons, badges) can't be sorted unless sortValue is given
  };

  const handleSort = (id) => {
    setSortConfig((prev) => ({
      key: id,
      direction: prev.key === id && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedData = useMemo(() => {
    const copy = [...data];
    if (!activeSortColumn) return copy;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    copy.sort((a, b) => dir * compareValues(getSortValue(activeSortColumn, a), getSortValue(activeSortColumn, b)));
    return copy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, activeSortColumn, sortConfig.direction]);

  /* ---------- filtering ---------- */
  const filteredData = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return sortedData;
    return sortedData.filter((item) =>
      columns.some((column) => {
        const value = column.filterValue ? column.filterValue(item) : column.selector(item);
        return isPrimitive(value) && String(value).toLowerCase().includes(q);
      })
    );
  }, [filterText, sortedData, columns]);

  /* ---------- pagination ---------- */
  const totalPages = Math.max(Math.ceil(filteredData.length / itemsPerPage), 1);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  useEffect(() => setCurrentPage(1), [filterText]);

  // If data shrinks (e.g. parent refetches) make sure we are not past the last page
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handlePageChange = (page) => setCurrentPage(Math.min(Math.max(page, 1), totalPages));

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const getRowKey = (row, index) => row.id ?? `${currentPage}-${index}`;

  const toggleExpanded = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  /* ---------- shared pieces ---------- */
  const renderPagination = () => {
    const items = getPageItems(currentPage, totalPages, isMobile ? 0 : 2);
    const total = filteredData.length;
    const startEntry = total === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endEntry = Math.min(currentPage * itemsPerPage, total);

    return (
      <nav className="dt-footer" aria-label="Table pagination">
        <div className="dt-entries" aria-live="polite">
          Showing {startEntry} to {endEntry} of {total} entries
        </div>

        <div className="dt-pagination">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary dt-page-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Prev
          </button>

          {items.map((item) =>
            typeof item === 'string' ? (
              <span key={item} className="dt-ellipsis" aria-hidden="true">
                …
              </span>
            ) : (
              <button
                type="button"
                key={item}
                onClick={() => handlePageChange(item)}
                aria-current={currentPage === item ? 'page' : undefined}
                className={`btn btn-sm dt-page-btn ${currentPage === item ? 'btn-active-page-no' : 'btn-outline-secondary'
                  }`}
              >
                {item}
              </button>
            )
          )}

          <button
            type="button"
            className="btn btn-sm btn-outline-secondary dt-page-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </nav>
    );
  };

  const renderToolbar = () => (
    <div className="dt-toolbar">
      <div className="dt-toolbar-left">
        <label htmlFor="dt-entries-per-page" className="dt-label">
          Show
        </label>
        <select
          id="dt-entries-per-page"
          className="form-select dt-select"
          value={itemsPerPage}
          onChange={handleItemsPerPageChange}
        >
          {[8, 10, 20, 30, 40, 50, 100, 500].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>

        {/* Mobile has no clickable headers, so sorting moves into the toolbar */}
        {isMobile && sortableColumns.length > 0 && (
          <div className="dt-sort-control">
            <label htmlFor="dt-sort-by" className="dt-label">
              Sort
            </label>
            <select
              id="dt-sort-by"
              className="form-select dt-select"
              value={sortConfig.key ?? ''}
              onChange={(e) =>
                e.target.value === ''
                  ? setSortConfig({ key: null, direction: 'asc' })
                  : setSortConfig({ key: e.target.value, direction: 'asc' })
              }
            >
              <option value="">Default</option>
              {sortableColumns.map(({ column, id }) => (
                <option key={id} value={id}>
                  {column.mobileLabel || column.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary dt-sort-dir"
              disabled={!sortConfig.key}
              onClick={() =>
                setSortConfig((prev) => ({ ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' }))
              }
              aria-label={`Sort direction: ${sortConfig.direction === 'asc' ? 'ascending' : 'descending'}`}
            >
              {sortConfig.direction === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        )}
      </div>

      <input
        type="search"
        placeholder="Search"
        aria-label="Search table"
        className="form-control search-bar"
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
      />
    </div>
  );

  /* ---------- desktop / tablet: table ---------- */
  const renderTable = () => (
    <div className="dt-table-wrap">
      <table className="table table-bordered table-hover dt-table">
        <thead>
          <tr>
            {columns.map((column, colIndex) => {
              const id = colId(column, colIndex);
              const sortable = column.name && column.sortable !== false;
              const active = sortConfig.key === id;
              return (
                <th
                  key={id}
                  scope="col"
                  onClick={sortable ? () => handleSort(id) : undefined}
                  onKeyDown={
                    sortable
                      ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSort(id);
                        }
                      }
                      : undefined
                  }
                  tabIndex={sortable ? 0 : undefined}
                  aria-sort={active ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  className={`sortable-column ${sortable ? '' : 'not-sortable'} ${active ? sortConfig.direction : ''
                    }`}
                >
                  {column.name}
                  {active && <span className={`sort-icon ${sortConfig.direction}`} />}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {paginatedData.length > 0 ? (
            paginatedData.map((row, rowIndex) => (
              <React.Fragment key={getRowKey(row, rowIndex)}>
                <tr
                  className={`main-row ${row.bgColor ? 'custom-bg' : rowIndex % 2 === 0 ? 'even-row' : 'odd-row'}`}
                  data-bgcolor={row.bgColor || ''}
                  style={row.bgColor ? { backgroundColor: row.bgColor } : undefined}
                >
                  {columns.map((column, colIndex) => {
                    const cellBg = typeof column.bgColor === 'function' ? column.bgColor(row) : column.bgColor || '';
                    return (
                      <td
                        key={colIndex}
                        className={`table-cell ${column.align || ''} ${column.bgColor ? 'custom-column-bg' : ''}`}
                        data-width={column.width || ''}
                        data-bgcolor={cellBg}
                        style={{
                          ...(column.width
                            ? { width: column.width, minWidth: column.width, maxWidth: column.maxWidth || column.width }
                            : {}),
                          ...(cellBg ? { backgroundColor: cellBg } : {}),
                          ...column.style,
                        }}
                      >
                        {column.selector(row)}
                      </td>
                    );
                  })}
                </tr>

                {row.nestedData && row.nestedData.length > 0 && (
                  <tr className="nested-row">
                    <td colSpan={columns.length}>
                      <div className="inner-table-container">
                        <table className="table table-bordered table-hover dt-inner-table">
                          <thead>
                            <tr className="inner-sub-table-header">
                              {innerColumns.map((innerColumn, colIndex) => (
                                <th
                                  key={colIndex}
                                  className={`inner-sub-table-th ${innerColumn.align || ''}`}
                                  data-width={innerColumn.width || ''}
                                >
                                  {innerColumn.name}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {row.nestedData.map((nestedRow, nestedIndex) => (
                              <tr key={nestedIndex} className="inner-data-row">
                                {innerColumns.map((innerColumn, colIndex) => {
                                  const bg =
                                    typeof innerColumn.bgColor === 'function'
                                      ? innerColumn.bgColor(nestedRow)
                                      : innerColumn.bgColor || '';
                                  const common = {
                                    key: colIndex,
                                    className: `inner-td ${innerColumn.align || ''}`,
                                    'data-bgcolor': bg,
                                    style: bg ? { backgroundColor: bg } : undefined,
                                  };

                                  if (innerColumn.rowSpan) {
                                    // Merged cell: only render on the row that starts the span
                                    if (!(nestedRow.rowSpan > 0)) return null;
                                    return (
                                      <td {...common} rowSpan={nestedRow.rowSpan}>
                                        {innerColumn.selector(nestedRow)}
                                      </td>
                                    );
                                  }
                                  return <td {...common}>{innerColumn.selector(nestedRow)}</td>;
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="text-center dt-empty">
                There are no records to display
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  /* ---------- mobile: cards ---------- */
  const renderCards = () => {
    const titleColumn = columns.find((c) => c.isTitle) || columns.find((c) => c.name);
    const actionColumns = columns.filter((c) => !c.name && c !== titleColumn);
    const fieldColumns = columns.filter((c) => c.name && c !== titleColumn && !c.hideOnMobile);

    if (paginatedData.length === 0) {
      return <div className="dt-empty dt-empty-card">There are no records to display</div>;
    }

    return (
      <ul className="dt-cards">
        {paginatedData.map((row, rowIndex) => {
          const key = getRowKey(row, rowIndex);
          const hasNested = row.nestedData && row.nestedData.length > 0;
          const isOpen = !!expanded[key];
          const titleBg = titleColumn && typeof titleColumn.bgColor === 'function' ? titleColumn.bgColor(row) : '';

          return (
            <li
              key={key}
              className="dt-card"
              data-bgcolor={row.bgColor || ''}
              style={row.bgColor ? { borderLeftColor: row.bgColor, backgroundColor: row.bgColor } : undefined}
            >
              {titleColumn && (
                <div className="dt-card-title" style={titleBg ? { backgroundColor: titleBg } : undefined}>
                  <span className="dt-card-title-label">{titleColumn.mobileLabel || titleColumn.name}</span>
                  <span className="dt-card-title-value">{titleColumn.selector(row)}</span>
                </div>
              )}

              <dl className="dt-card-fields">
                {fieldColumns.map((column, i) => {
                  const bg = typeof column.bgColor === 'function' ? column.bgColor(row) : column.bgColor || '';
                  return (
                    <div className="dt-card-field" key={i} style={bg ? { backgroundColor: bg } : undefined}>
                      <dt>{column.mobileLabel || column.name}</dt>
                      <dd>{column.selector(row)}</dd>
                    </div>
                  );
                })}
              </dl>

              {actionColumns.length > 0 && (
                <div className="dt-card-actions">
                  {actionColumns.map((column, i) => (
                    <div key={i}>{column.selector(row)}</div>
                  ))}
                </div>
              )}

              {hasNested && (
                <>
                  <button
                    type="button"
                    className="dt-card-toggle"
                    onClick={() => toggleExpanded(key)}
                    aria-expanded={isOpen}
                  >
                    {isOpen ? 'Hide details' : `View details (${row.nestedData.length})`}
                    <span className={`dt-chevron ${isOpen ? 'open' : ''}`} aria-hidden="true" />
                  </button>

                  {isOpen && (
                    <ul className="dt-subcards">
                      {row.nestedData.map((nestedRow, nestedIndex) => (
                        <li className="dt-subcard" key={nestedIndex}>
                          <dl>
                            {innerColumns.map((innerColumn, colIndex) => {
                              // rowSpan'd (merged) cells are only filled on the first nested row;
                              // in cards, skip them when empty so labels don't show blank values.
                              if (innerColumn.rowSpan && !(nestedRow.rowSpan > 0)) return null;
                              const bg =
                                typeof innerColumn.bgColor === 'function'
                                  ? innerColumn.bgColor(nestedRow)
                                  : innerColumn.bgColor || '';
                              return (
                                <div
                                  className="dt-card-field"
                                  key={colIndex}
                                  style={bg ? { backgroundColor: bg } : undefined}
                                >
                                  <dt>{innerColumn.name}</dt>
                                  <dd>{innerColumn.selector(nestedRow)}</dd>
                                </div>
                              );
                            })}
                          </dl>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className={`datatable-container ${isMobile ? 'is-mobile' : 'is-desktop'}`}>
      {renderToolbar()}
      {isMobile ? renderCards() : renderTable()}
      {renderPagination()}
    </div>
  );
};

export default Datatable;